import React, { useEffect, useState } from "react";
import { Storage } from "aws-amplify";

import { useNotify, useRecordContext } from "ra-core";
import { sanitizeFieldRestProps } from "ra-ui-materialui";

import { Typography, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";

import { logError } from "../../utils/appUtils";
import { getKeyFilename } from "../../utils/fileUtils";
import { AddIcon } from "./iconography";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "inline-block",
    marginTop: theme.spacing(0),
  },
  name: "RaFileField",
  linkText: { color: theme.palette.primary.main },
}));

const getFileFromStorage = async (
  keyValue,
  download,
  setSourceValue,
  setTitleValue,
  setStatus,
  notify
) => {
  try {
    // Request a signed URL that forces a download (Content-Disposition: attachment)
    // rather than letting the browser open the file inline.
    const options = download
      ? {
          contentDisposition: `attachment; filename="${getKeyFilename(
            keyValue
          ).replace(/"/g, "")}"`,
        }
      : {};
    const response = await Storage.get(keyValue, options);
    if (response) {
      setSourceValue(response);
      const title = getKeyFilename(keyValue);
      setTitleValue(title);
      return { sourceValue: response, titleValue: title };
    } else {
      setStatus("Error...");
      const error = `Storage error getting ${keyValue}`;
      logError("common:FileField:useEffect", error, notify);
    }
  } catch (error) {
    setStatus("Error...");
    logError("common:FileField:useEffect", error, notify);
  }
};

/**
 * Render a link to a file based on a path contained in a record field
 *
 * @example
 * import { FileField } from 'react-admin';
 *
 * <FileField source="url" title="title" />
 *
 * // renders the record { id: 123, url: 'doc.pdf', title: 'Presentation' } as
 * <div>
 *     <a href="doc.pdf" title="Presentation">Presentation</a>
 * </div>
 */
export const FileField = (props) => {
  const {
    className,
    classes: classesOverride,
    emptyText,
    source,
    title,
    src,
    target,
    download = false,
    ping,
    rel,
    onAddToken,
    tokenPrefix,
    ...rest
  } = props;
  const notify = useNotify();
  const record = useRecordContext(props);
  const classes = useStyles(props);

  const [keyValue, setKeyValue] = useState(null);
  const [sourceValue, setSourceValue] = useState(null);
  const [titleValue, setTitleValue] = useState(null);
  const [status, setStatus] = useState("Loading...");

  useEffect(() => {
    const sourceData = record[source];

    if (Array.isArray(sourceData)) {
      // array of saved files in FileField
      const keys = sourceData.map((item) => item.key);
      setKeyValue(keys);
    } else if (typeof sourceData === "object" && sourceData !== null) {
      // single saved file in FileField
      const key = sourceData.key;
      setKeyValue(key);
    } else if (record.key) {
      // saved file in FileInput
      setKeyValue(record.key);
    } else if (record.src) {
      // new file in FileInput
      setSourceValue(record.src);
      setTitleValue(record.title);
    }
  }, [record, source]);

  useEffect(() => {
    const fetchData = async () => {
      debugger;
      if (Array.isArray(keyValue)) {
        const data = await Promise.all(
          keyValue.map((key) =>
            getFileFromStorage(
              key,
              download,
              setSourceValue,
              setTitleValue,
              setStatus,
              notify
            )
          )
        );
        setSourceValue(data);
      } else if (typeof keyValue === "string") {
        getFileFromStorage(
          keyValue,
          download,
          setSourceValue,
          setTitleValue,
          setStatus,
          notify
        );
      }
    };

    if (keyValue) fetchData();
  }, [keyValue, download]);

  if (record?.src && sourceValue === null) {
    return (
      <Typography
        component="span"
        variant="body2"
        className={className}
        {...sanitizeFieldRestProps(rest)}
      >
        {status}
      </Typography>
    );
  }

  if (Array.isArray(sourceValue)) {
    debugger;
    return (
      <ul
        className={classnames(classes.root, className)}
        {...sanitizeFieldRestProps(rest)}
      >
        {sourceValue.map((file, index) => {
          const fileSourceValue = file.sourceValue;
          const fileTitleValue = file.titleValue;

          return (
            <li key={index}>
              <a
                href={fileSourceValue}
                title={fileTitleValue}
                target={download ? undefined : target}
                download={download ? fileTitleValue || true : undefined}
                ping={ping}
                rel={rel}
                className={classes.linkText}
              >
                {fileTitleValue}
              </a>
              {onAddToken && tokenPrefix && fileTitleValue && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const encodedName = encodeURIComponent(fileTitleValue);
                    onAddToken(
                      `[${fileTitleValue}](${tokenPrefix}/${encodedName})`
                    );
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div
      className={classnames(classes.root, className)}
      {...sanitizeFieldRestProps(rest)}
    >
      <a
        href={sourceValue}
        title={titleValue}
        target={download ? undefined : target}
        download={download ? titleValue || true : undefined}
        ping={ping}
        rel={rel}
        className={classes.linkText}
      >
        {titleValue}
      </a>
      {onAddToken && tokenPrefix && titleValue && (
        <IconButton
          size="small"
          onClick={() => {
            const encodedName = encodeURIComponent(titleValue);
            onAddToken(`[${titleValue}](${tokenPrefix}/${encodedName})`);
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};
